/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import { useQueries, type QueryClient } from '@tanstack/react-query';
import type { ReviewStatisticResponse, ReviewStatistics } from '../interface/types';
import { isRetryableError } from '@/features/customReviews/utils/retryStrategies';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import type { Locale } from '@/types/i18n';
import { combineReviewStatisticsForAnalytics } from '../utils/combineReviewStatisticsForAnalytics';
import { combineReviewStatisticsBySKU } from '../utils/combineReviewStatisticsBySKU';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/product/product-reviews-integration-bs/v1/review-statistics`;

type GetReviewStatisticsOptions = {
  skuNumbers: string[];
  locale?: Locale;
};

const selector = (statistics: ReviewStatisticResponse[]): ReviewStatistics =>
  statistics.reduce<ReviewStatistics>((acc, curr) => {
    acc[curr.skuNumber] = curr;
    return acc;
  }, {});

const getReviewStatistics = async (
  options: GetReviewStatisticsOptions,
  axiosInstance?: AxiosInstance,
) => {
  const { skuNumbers, locale = countryCodes.us } = options;

  if (!skuNumbers.length) {
    return [];
  }

  const response = await getAxios(axiosInstance).get<ReviewStatisticResponse[]>(URL, {
    params: {
      skuNumbers: skuNumbers.join(','),
      locale: locale.replace('-', '_'),
    },
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return response.data;
};

const {
  useData: useReviewStatisticsData,
  prefetch: prefetchReviewStatisticsData,
  query: reviewStatisticsQuery,
} = createQuery<ReviewStatisticResponse[], GetReviewStatisticsOptions>(
  'reviewStatistics',
  getReviewStatistics,
);

const getReviewStatisticsKey = ({ skuNumbers, locale }: GetReviewStatisticsOptions) => {
  return reviewStatisticsQuery.getFullKey({ skuNumbers, locale });
};

type ReviewStatisticsOptions<SelectData = ReviewStatistics> = GetReviewStatisticsOptions & {
  enabled?: boolean;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: unknown) => number);
  select?: (data: ReviewStatisticResponse[]) => SelectData;
};

export function useReviewStatistics<SelectData = ReviewStatistics>(
  options: ReviewStatisticsOptions<SelectData>,
) {
  const { enabled = true, select } = options;
  const locale = useLocale();

  const selectFn = select ?? (selector as (data: ReviewStatisticResponse[]) => SelectData);

  return useReviewStatisticsData<SelectData>({
    ...options,
    locale,
    enabled: enabled && locale !== countryCodes.ptBr,
    retry: isRetryableError,
    retryDelay: 1000,
    select: selectFn,
  });
}

export const prefetchReviewStatistics = (
  options: GetReviewStatisticsOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  return prefetchReviewStatisticsData(queryClient, options, axiosInstance);
};

type BatchedReviewStatisticsOptions = {
  skuNumbers: string[][];
  enabled?: boolean;
};

export const useReviewStatisticsBatches = ({
  skuNumbers: multipleSkuIds = [],
  enabled = true,
}: BatchedReviewStatisticsOptions) => {
  const locale = useLocale();

  return useQueries({
    queries: multipleSkuIds.map((skuNumbers) => ({
      enabled: enabled && skuNumbers.length > 0 && locale !== countryCodes.ptBr,
      queryKey: getReviewStatisticsKey({
        skuNumbers,
        locale,
      }),
      queryFn: (ctx) => getReviewStatistics(ctx.queryKey[1] as GetReviewStatisticsOptions),
      retry: isRetryableError,
      retryDelay: 1000,
      staleTime: Infinity,
    })),
    combine: combineReviewStatisticsBySKU,
  });
};

export const useReviewStatisticsForAnalytics = ({
  skuNumbers: multipleSkuIds = [],
  enabled = true,
}: BatchedReviewStatisticsOptions) => {
  const locale = useLocale();

  return useQueries({
    queries: multipleSkuIds.map((skuNumbers) => ({
      enabled: enabled && skuNumbers.length > 0,
      queryKey: getReviewStatisticsKey({
        skuNumbers,
        locale,
      }),
      queryFn: (ctx) => getReviewStatistics(ctx.queryKey[1] as GetReviewStatisticsOptions),
      retry: isRetryableError,
      retryDelay: 1000,
      staleTime: Infinity,
    })),
    combine: combineReviewStatisticsForAnalytics,
  });
};
